import { StatsInfo } from "src/components/compounds/GameStats/stat-info-type";
import { GridItem, PlayerWithLinkColumn, PlayerWithoutLinkColumn } from "../common-columns";
import {
  fourDigitNumberFormatter,
  percentFormatter,
  inningsFormatter,
  eventDateFormatter,
  velocityFormatter,
} from "../formatters";

export interface PitchingStandardFormattedRow {
  player: {
    id: string;
    teamID: string;
    name: string;
    status: string;
  };
  "GP:P": number;
  GS: number;
  BF: number;
  "#P": number;
  W: number;
  L: number;
  SV: number;
  SVO: number;
  BS: number;
  ["SV%"]: number;
  H: number;
  R: number;
  ER: number;
  BB: number;
  SO: number;
  ["K-L"]: number;
  HBP: number;
  ERA: number;
  WHIP: number;
  LOB: number;
  BK: number;
  PIK: number;
  CS: number;
  SB: number;
  WP: number;
  BAA: number;
  MPHFB: number;
  MPHCT: number;
  MPHCB: number;
  MPHSL: number;
  MPHCH: number;
  MPHOS: number;
}

export const pitchingStandardGridDataFormatted = (grid: GridItem[]): PitchingStandardFormattedRow[] => {
  return grid
    .filter(({ defense }) => defense)
    .map(({ person_id, team_id, name, eventID, date, offense, defense }) => {
      return {
        player: {
          id: person_id,
          teamID: team_id,
          name,
          eventID,
          status: "active",
        },
        // Read by the per-game Date column; undefined (and ignored) in season/box-score views.
        event: date,
        IP: defense.ip,
        "GP:P": defense["GP:P"],
        GS: defense.gs,
        BF: defense.bf,
        "#P": defense["#P"],
        W: defense.w,
        L: defense.l,
        SV: defense.sv,
        SVO: defense.svo,
        BS: defense.bs,
        // eslint-disable-next-line
        ["SV%"]: defense["SV%"],
        H: defense.h,
        R: defense.r,
        ER: defense.er,
        BB: defense.bb,
        SO: defense.so,
        // eslint-disable-next-line
        ["K-L"]: defense.sol,
        HBP: defense.hbp,
        ERA: defense.era,
        WHIP: defense.whip,
        LOB: defense.lob,
        BK: defense.bk,
        PIK: defense.pik,
        CS: defense.cs,
        SB: defense.sb,
        WP: defense.wp,
        BAA: defense.baa,
        MPHFB: defense.MPHFB,
        MPHCT: defense.MPHCT,
        MPHCB: defense.MPHCB,
        MPHSL: defense.MPHSL,
        MPHCH: defense.MPHCH,
        MPHOS: defense.MPHOS,
      };
    });
};

const CorePitchingStandard: StatsInfo[] = [
  { name: "GS", field: "GS", def: "Games started" },
  { name: "BF", field: "BF", def: "Total batters faced" },
  { name: "#P", field: "#P", def: "Total pitches" },
  { name: "W", field: "W", def: "Wins" },
  { name: "L", field: "L", def: "Losses" },
  { name: "SV", field: "SV", def: "Saves" },
  { name: "SVO", field: "SVO", def: "Save opportunities" },
  { name: "BS", field: "BS", def: "Blown saves" },
  { name: "SV%", field: "SV%", def: "Save percentage", valueFormatter: percentFormatter },
  { name: "H", field: "H", def: "Hits allowed" },
  { name: "R", field: "R", def: "Runs allowed" },
  { name: "ER", field: "ER", def: "Earned runs allowed" },
  { name: "BB", field: "BB", def: "Base on balls (walks)" },
  { name: "SO", field: "SO", def: "Strikeouts" },
  { name: "K-L", field: "K-L", def: "Strikeouts looking" },
  { name: "HBP", field: "HBP", def: "Hit batters" },
  { name: "ERA", field: "ERA", def: "Earned run average", valueFormatter: fourDigitNumberFormatter },
  {
    name: "WHIP",
    field: "WHIP",
    def: "Walks plus hits per innings pitched",
    valueFormatter: fourDigitNumberFormatter,
  },
  { name: "LOB", field: "LOB", def: "Runners left on base" },
  { name: "BK", field: "BK", def: "Balks" },
  { name: "PIK", field: "PIK", def: "Runners picked off" },
  { name: "CS", field: "CS", def: "Runners caught stealing" },
  { name: "SB", field: "SB", def: "Stolen bases allowed" },
  {
    name: "SB%",
    field: "SB%",
    def: "Stolen bases allowed percentage",
    valueFormatter: percentFormatter,
  },
  { name: "WP", field: "WP", def: "Wild pitches" },
  {
    name: "BAA",
    field: "BAA",
    def: "Opponent batting average",
    valueFormatter: fourDigitNumberFormatter,
  },
  { name: "MPHFB", field: "MPHFB", def: "Fastball average velocity", valueFormatter: velocityFormatter },
  { name: "MPHCT", field: "MPHCT", def: "Cutter average velocity", valueFormatter: velocityFormatter },
  { name: "MPHCB", field: "MPHCB", def: "Curveball average velocity", valueFormatter: velocityFormatter },
  { name: "MPHSL", field: "MPHSL", def: "Slider average velocity", valueFormatter: velocityFormatter },
  { name: "MPHCH", field: "MPHCH", def: "Changeup average velocity", valueFormatter: velocityFormatter },
  { name: "MPHOS", field: "MPHOS", def: "Offspeed average velocity", valueFormatter: velocityFormatter },
];

export const PitchingStandard: StatsInfo[] = (
  [
    PlayerWithLinkColumn,
    {
      name: "IP",
      field: "IP",
      def: "Innings pitched",
      valueFormatter: inningsFormatter,
      sort: "desc",
    },
    { name: "GP", field: "GP:P", def: "Games pitched" },
  ] as StatsInfo[]
).concat(CorePitchingStandard);

export const EventPitchingStandard: StatsInfo[] = (
  [
    {
      name: "Date",
      field: "event",
      valueFormatter: eventDateFormatter,
      columnProps: {
        width: 100,
        pinned: true,
        cellStyle: { textAlign: "left" },
        sort: "asc",
      },
    },
    {
      name: "OPPONENT",
      field: "opponent",
      columnProps: {
        width: 260,
        cellStyle: { textAlign: "left" },
        headerClass: "left-aligned-column-header",
      },
    },
    {
      name: "IP",
      field: "IP",
      def: "Innings pitched",
      valueFormatter: inningsFormatter,
    },
  ] as StatsInfo[]
).concat(CorePitchingStandard);

export const GamePitchingStandard: StatsInfo[] = [
  PlayerWithoutLinkColumn,
  {
    name: "IP",
    field: "IP",
    def: "Innings pitched",
    valueFormatter: inningsFormatter,
    columnProps: {
      sort: "desc",
    },
  },
  { name: "GS", field: "GS", def: "Games started" },
  { name: "BF", field: "BF", def: "Total batters faced" },
  { name: "#P", field: "#P", def: "Total pitches" },
  { name: "TB", field: "TB", def: "Total balls" },
  { name: "TS", field: "TS", def: "Total strikes" },
  { name: "H", field: "H", def: "Hits allowed" },
  { name: "R", field: "R", def: "Runs allowed" },
  { name: "ER", field: "ER", def: "Earned runs allowed" },
  { name: "BB", field: "BB", def: "Base on balls (walks)" },
  { name: "SO", field: "SO", def: "Strikeouts" },
  { name: "K-L", field: "K-L", def: "Strikeouts looking" },
  { name: "HBP", field: "HBP", def: "Hit batters" },
  { name: "LOB", field: "LOB", def: "Runners left on base" },
  { name: "BK", field: "BK", def: "Balks" },
  { name: "PIK", field: "PIK", def: "Runners picked off" },
  { name: "CS", field: "CS", def: "Runners caught stealing" },
  { name: "SB", field: "SB", def: "Stolen bases allowed" },
  { name: "WP", field: "WP", def: "Wild pitches" },
  { name: "W", field: "W", def: "Wins" },
  { name: "L", field: "L", def: "Losses" },
  { name: "SV", field: "SV", def: "Saves" },
  { name: "BS", field: "BS", def: "Blown saves" },
  {
    name: "BAA",
    field: "BAA",
    def: "Opponent batting average",
    valueFormatter: fourDigitNumberFormatter,
  },
];
